import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type Node,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useReactFlow,
  type Viewport,
} from '@xyflow/react';
import { useIntl } from 'react-intl';
import '@xyflow/react/dist/style.css';
import {
  ConfigurationMapEdge,
  ConfigurationMapField,
  ConfigurationMapHook,
  ConfigurationMapRule,
} from '@rossum/api-client/internal';
import { IconAlertTriangle, IconFocusCentered } from '@rossum/ui/icons/tabler';
import {
  Box,
  Chip,
  IconButton,
  SvgIcon,
  Tooltip,
  useTheme,
} from '@rossum/ui/material';
import { computeConnected } from './computeConnected';
import { computeCycles } from './computeCycles';
import { computeDisplayEdges } from './computeDisplayEdges';
import { computeLayout, type LayoutResult } from './computeLayout';
import { FieldNode } from './FieldNode';
import {
  computeHomeViewport,
  GraphContext,
  type GraphContextState,
} from './GraphContext';
import { HookNode } from './HookNode';
import { RuleNode } from './RuleNode';

type ConfigMapGraphProps = {
  fields: ConfigurationMapField[];
  edges: ConfigurationMapEdge[];
  rules: ConfigurationMapRule[];
  hooks: ConfigurationMapHook[];
  nodeUrlMap: Record<string, string>;
  showRules: boolean;
  showHooks: boolean;
};

const nodeTypes = { field: FieldNode, rule: RuleNode, hook: HookNode };

/** Thresholds for detecting viewport changes from the home position. */
const ZOOM_EPSILON = 0.01;
const PAN_EPSILON = 1;

const ConfigMapGraphInner = ({
  fields,
  edges,
  rules,
  hooks,
  nodeUrlMap,
  showRules,
  showHooks,
}: ConfigMapGraphProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const { setViewport, fitView } = useReactFlow();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isZoomChanged, setIsZoomChanged] = useState(false);

  const ruleIds = useMemo(() => new Set(rules.map(r => r.id)), [rules]);
  const hookIds = useMemo(() => new Set(hooks.map(h => h.id)), [hooks]);

  // When showRules/showHooks is false, exclude edges where either endpoint
  // is a rule/hook node — otherwise edges render as dangling lines.
  const activeEdges = useMemo(
    () =>
      edges.filter(
        e =>
          (showRules || (!ruleIds.has(e.source) && !ruleIds.has(e.target))) &&
          (showHooks || (!hookIds.has(e.source) && !hookIds.has(e.target)))
      ),
    [edges, showRules, showHooks, ruleIds, hookIds]
  );

  const { connectedIds, connectedEdges } = useMemo(() => {
    if (!selectedNodeId)
      return {
        connectedIds: new Set<string>(),
        connectedEdges: new Set<string>(),
      };
    const result = computeConnected(
      selectedNodeId,
      activeEdges,
      hookIds,
      ruleIds
    );
    return { connectedIds: result.nodeIds, connectedEdges: result.edges };
  }, [selectedNodeId, activeEdges, hookIds, ruleIds]);

  const getNodeUrl = useCallback((id: string) => nodeUrlMap[id], [nodeUrlMap]);

  const { cycleNodeIds, cycleEdgeKeys, cycleCount } = useMemo(
    () => computeCycles(activeEdges, fields),
    [activeEdges, fields]
  );

  const graphContext = useMemo<GraphContextState>(
    () => ({ selectedNodeId, connectedIds, cycleNodeIds, getNodeUrl }),
    [selectedNodeId, connectedIds, cycleNodeIds, getNodeUrl]
  );

  const externalFieldIds = useMemo(
    () => new Set(fields.filter(f => f.type === 'external').map(f => f.id)),
    [fields]
  );

  const layoutResult: LayoutResult = useMemo(
    () =>
      computeLayout(
        fields,
        activeEdges,
        showRules ? rules : [],
        showHooks ? hooks : []
      ),
    [fields, activeEdges, rules, hooks, showRules, showHooks]
  );

  const initialNodes = layoutResult.nodes;

  const homeViewport = useMemo(
    () => computeHomeViewport(layoutResult.fieldBandY),
    [layoutResult.fieldBandY]
  );

  const edgeColors = useMemo(
    () => ({
      default: theme.palette.divider,
      highlighted: theme.palette.primary.main,
      external: theme.palette.error.main,
      runAfter: theme.palette.text.disabled,
      cycle: theme.palette.error.main,
    }),
    [
      theme.palette.divider,
      theme.palette.primary.main,
      theme.palette.error.main,
      theme.palette.text.disabled,
    ]
  );

  const displayEdges = useMemo(
    () =>
      computeDisplayEdges(
        activeEdges,
        selectedNodeId,
        connectedEdges,
        externalFieldIds,
        hookIds,
        cycleEdgeKeys,
        edgeColors
      ),
    [
      activeEdges,
      selectedNodeId,
      connectedEdges,
      externalFieldIds,
      hookIds,
      cycleEdgeKeys,
      edgeColors,
    ]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);

  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setSelectedNodeId(null);
    setViewport(homeViewport, { duration: 300 });
  }, [fields, showRules, showHooks, homeViewport, setViewport]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNodeId(prev => (prev === node.id ? null : node.id));
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const onViewportChange = useCallback(
    (viewport: Viewport) => {
      setIsZoomChanged(
        Math.abs(viewport.zoom - homeViewport.zoom) > ZOOM_EPSILON ||
          Math.abs(viewport.x - homeViewport.x) > PAN_EPSILON ||
          Math.abs(viewport.y - homeViewport.y) > PAN_EPSILON
      );
    },
    [homeViewport]
  );

  const onResetViewport = useCallback(() => {
    setViewport(homeViewport, { duration: 300 });
  }, [setViewport, homeViewport]);

  const onFocusCycles = useCallback(() => {
    fitView({
      nodes: Array.from(cycleNodeIds, id => ({ id })),
      duration: 300,
      padding: 0.2,
      maxZoom: 1,
    });
  }, [fitView, cycleNodeIds]);

  return (
    <GraphContext.Provider value={graphContext}>
      <ReactFlow
        nodes={nodes}
        edges={displayEdges}
        onNodesChange={onNodesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onViewportChange={onViewportChange}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        edgesFocusable={false}
        edgesReconnectable={false}
        panOnScroll
        zoomOnScroll={false}
        zoomOnDoubleClick={false}
        defaultViewport={homeViewport}
        minZoom={0.1}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        // eslint-disable-next-line no-restricted-syntax -- ReactFlow does not support sx
        style={{
          backgroundColor: theme.palette.background.default,
          borderRadius: 8,
          height: '100%',
        }}
      />
      {cycleCount > 0 && (
        <Tooltip
          title={intl.formatMessage({
            id: 'containers.settings.queues.queue.overview.cycles.bannerTooltip',
          })}
        >
          <Chip
            size="small"
            color="error"
            icon={
              <SvgIcon fontSize="small">
                <IconAlertTriangle />
              </SvgIcon>
            }
            label={intl.formatMessage(
              {
                id: 'containers.settings.queues.queue.overview.cycles.banner',
              },
              { count: cycleCount }
            )}
            onClick={onFocusCycles}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          />
        </Tooltip>
      )}
      {isZoomChanged && (
        <Tooltip
          title={intl.formatMessage({
            id: 'containers.settings.queues.queue.overview.resetView',
          })}
        >
          <IconButton
            onClick={onResetViewport}
            size="medium"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <SvgIcon fontSize="medium">
              <IconFocusCentered />
            </SvgIcon>
          </IconButton>
        </Tooltip>
      )}
    </GraphContext.Provider>
  );
};

export const ConfigMapGraph = (props: ConfigMapGraphProps) => {
  return (
    <ReactFlowProvider>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          // ReactFlow requires a container with explicit dimensions.
          // PageLayoutV2 uses overflowY: auto, so flex: 1 alone resolves
          // to 0 height. We use a viewport-based height as the fallback.
          height: 'calc(100dvh - 280px)',
          '& .react-flow__edge': { pointerEvents: 'none' },
        }}
      >
        <ConfigMapGraphInner {...props} />
      </Box>
    </ReactFlowProvider>
  );
};
